import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import {
  ListChecks, Loader2, Wand2, Sparkles, Check, Download,
} from "lucide-react";
import { downloadText } from "@/lib/download";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const INSIGHTS_RANGES = [
  { key: "7d",  label: "7d"  },
  { key: "30d", label: "30d" },
  { key: "all", label: "All" },
];
export const INSIGHTS_SENT = {
  positive: { bg: "bg-emerald-500", text: "text-emerald-700", label: "Positive" },
  neutral:  { bg: "bg-slate-400",   text: "text-slate-700",   label: "Neutral" },
  negative: { bg: "bg-red-500",     text: "text-red-700",     label: "Negative" },
};
export const INSIGHTS_INT = {
  hot:  { bg: "bg-rose-500",  text: "text-rose-700",  label: "Hot" },
  warm: { bg: "bg-amber-500", text: "text-amber-700", label: "Warm" },
  cold: { bg: "bg-sky-500",   text: "text-sky-700",   label: "Cold" },
};
export const INSIGHTS_OBJ_LABEL = { price: "Price", timing: "Timing", not_interested: "Not interested", other: "Other" };

export function InsightsRangePicker({ value, onChange, testid }) {
  return (
    <div className="inline-flex overflow-hidden rounded-full border border-slate-200 bg-white" data-testid={testid}>
      {INSIGHTS_RANGES.map((r) => (
        <button key={r.key} type="button" onClick={() => onChange(r.key)}
          data-testid={`${testid}-${r.key}`}
          className={`px-2.5 py-0.5 text-[11px] font-medium transition-colors ${value === r.key ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-50"}`}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

function InsightsHistogramRow({ tone, filterType, filterValue, count, total, testid, onDrill }) {
  const bar = (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full ${tone.bg}`}
           style={{ width: `${count && total ? Math.max(4, Math.round((count / total) * 100)) : 0}%` }} />
    </div>
  );
  return (
    <button type="button"
      onClick={() => count > 0 && onDrill?.({ [filterType]: filterValue })}
      disabled={!count || !onDrill}
      data-testid={testid}
      className={`flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-xs transition-colors ${count && onDrill ? "cursor-pointer hover:bg-slate-50" : "cursor-default opacity-90"}`}>
      <div className={`w-20 shrink-0 font-medium ${tone.text}`}>{tone.label}</div>
      <div className="flex-1">{bar}</div>
      <div className="w-8 shrink-0 text-right tabular-nums text-slate-500">{count}</div>
    </button>
  );
}

export function InsightsHistogram({ data, onDrill }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">Sentiment</div>
        <div className="space-y-1">
          {["positive", "neutral", "negative"].map((k) => (
            <InsightsHistogramRow key={k} tone={INSIGHTS_SENT[k]} filterType="sentiment" filterValue={k}
                 count={data.sentiment_counts[k]} total={data.total_scored} onDrill={onDrill}
                 testid={`ai-outbound-insights-sentiment-${k}`} />
          ))}
        </div>
      </div>
      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">Interest</div>
        <div className="space-y-1">
          {["hot", "warm", "cold"].map((k) => (
            <InsightsHistogramRow key={k} tone={INSIGHTS_INT[k]} filterType="interest" filterValue={k}
                 count={data.interest_counts[k]} total={data.total_scored} onDrill={onDrill}
                 testid={`ai-outbound-insights-interest-${k}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function InsightsObjectionsRow({ data, onDrill, onBulkSuggest }) {
  if (!data.objections_top?.length) return null;
  return (
    <div className="mt-4">
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">Top objections</div>
        {onBulkSuggest && (
          <button type="button" onClick={onBulkSuggest}
            data-testid="ai-outbound-insights-bulk-suggest"
            className="inline-flex items-center gap-1 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary hover:bg-primary/10">
            <Wand2 className="h-3 w-3" /> Suggest for all
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="ai-outbound-insights-objections">
        {data.objections_top.map((o) => (
          <button key={o.key} type="button" onClick={() => onDrill?.({ objection: o.key })}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 transition-colors hover:bg-primary/10 hover:text-primary"
            data-testid={`ai-outbound-insights-objection-${o.key}`}>
            {INSIGHTS_OBJ_LABEL[o.key] || o.key}
            <span className="rounded-full bg-slate-200 px-1.5 text-[10px] text-slate-600">{o.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ScoredCallsDialog({ open, onClose, campaignId, filters, since, onSuggest }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open || !campaignId) return;
    let cancelled = false;
    setRows(null);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (since) params.set("since", since);
        Object.entries(filters || {}).forEach(([k, v]) => { if (v) params.set(k, v); });
        const { data } = await api.get(`/outbound/campaigns/${campaignId}/scored-calls?${params.toString()}`);
        if (!cancelled) setRows(data.calls || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, campaignId, since, filters]);

  const filterLabel = () => {
    const parts = [];
    if (filters?.sentiment) parts.push(`Sentiment: ${INSIGHTS_SENT[filters.sentiment]?.label || filters.sentiment}`);
    if (filters?.interest)  parts.push(`Interest: ${INSIGHTS_INT[filters.interest]?.label || filters.interest}`);
    if (filters?.objection) parts.push(`Objection: ${INSIGHTS_OBJ_LABEL[filters.objection] || filters.objection}`);
    return parts.join(" · ") || "All scored calls";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="ai-outbound-insights-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary" /> Scored calls
          </DialogTitle>
          <DialogDescription>{filterLabel()} · {since === "all" || !since ? "All time" : `Last ${since}`}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[520px] overflow-y-auto">
          {rows === null ? (
            <div className="flex items-center justify-center py-8 text-xs text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">No matching calls in this window.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="ai-outbound-insights-drill-list">
              {rows.map((r) => (
                <li key={r.call_sid} className="flex items-start gap-3 px-1 py-2.5"
                    data-testid={`ai-outbound-insights-drill-row-${r.call_sid}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${INSIGHTS_SENT[r.sentiment]?.bg.replace("500", "100")} ${INSIGHTS_SENT[r.sentiment]?.text}`}>
                        {INSIGHTS_SENT[r.sentiment]?.label || r.sentiment}
                      </span>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${INSIGHTS_INT[r.interest_level]?.bg.replace("500", "100")} ${INSIGHTS_INT[r.interest_level]?.text}`}>
                        {INSIGHTS_INT[r.interest_level]?.label || r.interest_level}
                      </span>
                      {(r.objections || []).map((o) => (
                        <span key={o} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                          {INSIGHTS_OBJ_LABEL[o] || o}
                        </span>
                      ))}
                      <span className="ml-auto font-mono text-[11px] text-slate-400">{r.to}</span>
                    </div>
                    <div className="mt-1 text-xs italic text-slate-700">&ldquo;{r.summary_line}&rdquo;</div>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-400">
                      <span>{r.duration_sec ? `${r.duration_sec}s` : "—"}</span>
                      <span>·</span>
                      <span>{r.scored_at ? new Date(r.scored_at).toLocaleString() : ""}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex items-center gap-2">
            {rows && rows.length > 0 && (
              <>
                <Button variant="outline" onClick={() => onSuggest?.()}
                        data-testid="ai-outbound-insights-drill-suggest"
                        className="border-primary/30 text-primary hover:bg-primary/10">
                  <Wand2 className="h-4 w-4" /> Suggest a script tweak
                </Button>
                <Button variant="outline" onClick={() => {
                    const header = ["call_sid","to","sentiment","interest_level","objections","summary_line","duration_sec","scored_at"];
                    const esc = (v) => `"${String(v ?? "").replace(/"/g,'""')}"`;
                    const lines = [header.join(",")].concat(
                      rows.map((r) => [
                        r.call_sid, r.to, r.sentiment, r.interest_level,
                        (r.objections || []).join("|"),
                        r.summary_line, r.duration_sec,
                        r.scored_at ? new Date(r.scored_at).toISOString() : "",
                      ].map(esc).join(","))
                    );
                    const filterTag = [filters?.sentiment, filters?.interest, filters?.objection].filter(Boolean).join("-") || "all";
                    downloadText(lines.join("\n"), `scored-calls-${filterTag}-${new Date().toISOString().slice(0,10)}.csv`);
                    toast.success(`Exported ${rows.length} call${rows.length === 1 ? "" : "s"}`);
                  }}
                  data-testid="ai-outbound-insights-drill-export">
                  <Download className="h-4 w-4" /> Export CSV
                </Button>
              </>
            )}
          </div>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-insights-drill-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScriptSuggestionDialog({ open, onClose, campaignId, filters, since, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState("");
  const [applying, setApplying] = useState(false);
  const [alsoTemplate, setAlsoTemplate] = useState(false);

  useEffect(() => {
    if (!open || !campaignId) return;
    let cancelled = false;
    setLoading(true); setSuggestion(null); setError(""); setAlsoTemplate(false);
    (async () => {
      try {
        const { data } = await api.post(`/outbound/campaigns/${campaignId}/suggest-script`, {
          since: since || null,
          sentiment: filters?.sentiment || null,
          interest: filters?.interest || null,
          objection: filters?.objection || null,
        });
        if (!cancelled) setSuggestion(data);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate suggestion");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, campaignId, since, filters]);

  const apply = async () => {
    if (!suggestion) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/outbound/campaigns/${campaignId}/append-objection`, {
        trigger: suggestion.trigger,
        response: suggestion.response,
        based_on: suggestion.based_on || [],
        also_update_template: alsoTemplate && suggestion.template_editable,
      });
      const bits = [];
      if (data.campaign_updated) bits.push("campaign");
      if (data.template_updated) bits.push("template");
      toast.success(`Added rebuttal to ${bits.join(" + ")}.`);
      onApplied?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to append");
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="ai-outbound-suggest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" /> Suggested script tweak
          </DialogTitle>
          <DialogDescription>
            Claude analyzed the drilled-in transcripts and drafted an objection rebuttal you can add to this campaign.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-xs text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Analyzing transcripts…
          </div>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700"
               data-testid="ai-outbound-suggest-error">
            {error}
          </div>
        ) : suggestion ? (
          <div className="space-y-3">
            <div className="rounded-md border border-slate-200 p-3">
              <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">Trigger</div>
              <div className="text-sm font-medium text-slate-800" data-testid="ai-outbound-suggest-trigger">
                {suggestion.trigger}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">Rebuttal</div>
              <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed"
                   data-testid="ai-outbound-suggest-response">
                {suggestion.response}
              </div>
            </div>
            {suggestion.rationale && (
              <div className="text-[11px] italic text-slate-500" data-testid="ai-outbound-suggest-rationale">
                Why: {suggestion.rationale}
              </div>
            )}
            <div className="text-[11px] text-slate-400">
              Based on {suggestion.based_on?.length || 0} scored call{(suggestion.based_on?.length || 0) === 1 ? "" : "s"}.
            </div>
            {suggestion.template_id && suggestion.template_editable && (
              <label className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 p-2 text-xs text-slate-700 cursor-pointer"
                     data-testid="ai-outbound-suggest-also-template-label">
                <input type="checkbox" checked={alsoTemplate}
                       onChange={(e) => setAlsoTemplate(e.target.checked)}
                       data-testid="ai-outbound-suggest-also-template"
                       className="h-3.5 w-3.5 accent-primary" />
                <span>
                  Also add to the source template so future campaigns cloned from it get this rebuttal.
                </span>
              </label>
            )}
            {suggestion.template_id && !suggestion.template_editable && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-500"
                   data-testid="ai-outbound-suggest-template-locked">
                Source template is view-only for you. Staff can edit templates.
              </div>
            )}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-suggest-close">
            Cancel
          </Button>
          <Button onClick={apply} disabled={!suggestion || applying}
                  data-testid="ai-outbound-suggest-apply">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {alsoTemplate ? "Add to campaign + template" : "Add to campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkSuggestDialog({ open, onClose, campaignId, since, onAppliedAny }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [applying, setApplying] = useState({});
  const [applied, setApplied] = useState({});

  useEffect(() => {
    if (!open || !campaignId) return;
    let cancelled = false;
    setLoading(true); setItems([]); setApplied({});
    (async () => {
      try {
        const { data } = await api.post(`/outbound/campaigns/${campaignId}/suggest-script/bulk`,
                                        { since: since || null, top_n: 3 });
        if (!cancelled) setItems(data.suggestions || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk suggest failed");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, campaignId, since]);

  const applyOne = async (item, alsoTemplate = false) => {
    if (!item.suggestion) return;
    setApplying((m) => ({ ...m, [item.objection_key]: true }));
    try {
      await api.post(`/outbound/campaigns/${campaignId}/append-objection`, {
        trigger: item.suggestion.trigger,
        response: item.suggestion.response,
        based_on: item.suggestion.based_on || [],
        also_update_template: alsoTemplate && item.suggestion.template_editable,
      });
      setApplied((m) => ({ ...m, [item.objection_key]: true }));
      toast.success(`Added rebuttal for ${INSIGHTS_OBJ_LABEL[item.objection_key] || item.objection_key}`);
      onAppliedAny?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to append");
    } finally {
      setApplying((m) => ({ ...m, [item.objection_key]: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="ai-outbound-bulk-suggest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" /> Suggest for top objections
          </DialogTitle>
          <DialogDescription>
            Claude drafted a rebuttal for each of the top objections in this campaign
            {since && since !== "all" ? ` over the last ${since}` : ""}. Add the ones you like.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-xs text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Generating suggestions… (this can take 5-15s)
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-400">No objections found in this window.</div>
        ) : (
          <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1"
               data-testid="ai-outbound-bulk-suggest-list">
            {items.map((item) => {
              const label = INSIGHTS_OBJ_LABEL[item.objection_key] || item.objection_key;
              const isApplied = !!applied[item.objection_key];
              const isApplying = !!applying[item.objection_key];
              return (
                <div key={item.objection_key}
                     className="rounded-lg border border-slate-200 p-3"
                     data-testid={`ai-outbound-bulk-suggest-item-${item.objection_key}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <div className="text-xs font-semibold text-slate-800">
                      {label} <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-[10px] font-medium text-slate-600">{item.count}</span>
                    </div>
                    {isApplied && <span className="text-[10px] font-semibold uppercase text-emerald-600">Added</span>}
                  </div>
                  {item.error ? (
                    <div className="text-[11px] text-red-600" data-testid={`ai-outbound-bulk-suggest-error-${item.objection_key}`}>
                      Skipped: {item.error}
                    </div>
                  ) : (
                    <>
                      <div className="text-xs font-medium text-slate-700 mt-1">
                        {item.suggestion.trigger}
                      </div>
                      <div className="mt-1 rounded bg-slate-50 p-2 text-xs text-slate-700 whitespace-pre-wrap"
                           data-testid={`ai-outbound-bulk-suggest-response-${item.objection_key}`}>
                        {item.suggestion.response}
                      </div>
                      {item.suggestion.rationale && (
                        <div className="mt-1 text-[11px] italic text-slate-500">
                          Why: {item.suggestion.rationale}
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-end gap-2">
                        {item.suggestion.template_editable && (
                          <Button variant="outline" size="sm" disabled={isApplied || isApplying}
                                  onClick={() => applyOne(item, true)}
                                  data-testid={`ai-outbound-bulk-suggest-apply-both-${item.objection_key}`}>
                            {isApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                            Add to campaign + template
                          </Button>
                        )}
                        <Button size="sm" disabled={isApplied || isApplying}
                                onClick={() => applyOne(item, false)}
                                data-testid={`ai-outbound-bulk-suggest-apply-${item.objection_key}`}>
                          {isApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          {isApplied ? "Added" : "Add to campaign"}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-bulk-suggest-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CampaignInsightsPanel({ campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = usePersistedState("ai-outbound-insights.range", "30d");
  const [drill, setDrill] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/outbound/campaigns/${campaignId}/insights?since=${range}`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [campaignId, range]);

  if (loading || !data || !data.total_scored) return null;

  return (
    <Card className="border-slate-200 p-4" data-testid="ai-outbound-campaign-insights">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Campaign Insights</h3>
        </div>
        <div className="flex items-center gap-2">
          <InsightsRangePicker value={range} onChange={setRange} testid="ai-outbound-insights-range" />
          <span className="text-[11px] text-slate-400" data-testid="ai-outbound-insights-count">
            {data.total_scored} scored · {data.total_calls} total
          </span>
        </div>
      </div>

      <InsightsHistogram data={data} onDrill={setDrill} />
      <InsightsObjectionsRow data={data} onDrill={setDrill} onBulkSuggest={() => setBulkOpen(true)} />

      {data.recent_summaries.length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">Recent call notes</div>
          <ul className="space-y-1.5" data-testid="ai-outbound-insights-summaries">
            {data.recent_summaries.map((s) => (
              <li key={s.call_sid} className="text-xs text-slate-600">
                <span className="mr-1 inline-block rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 capitalize">
                  {(s.interest_level || "—")}
                </span>
                &ldquo;{s.summary_line}&rdquo;
                {s.to && <span className="ml-1 font-mono text-slate-400">· {s.to}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ScoredCallsDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        campaignId={campaignId}
        filters={drill || {}}
        since={range}
        onSuggest={() => setSuggestOpen(true)}
      />
      <ScriptSuggestionDialog
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        campaignId={campaignId}
        filters={drill || {}}
        since={range}
      />
      <BulkSuggestDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        campaignId={campaignId}
        since={range}
      />
    </Card>
  );
}
